import { useCallback, useEffect, useRef, useState } from 'react';
import type { ChangeEvent, KeyboardEvent, ReactElement, RefObject } from 'react';

import styles from './PrimaryPinputField.module.css';

/**
 * Перенос `lib/src/presentation/widgets/primary_pinput_field.dart`.
 *
 * Пакета `pinput` в вебе нет, поэтому шесть ячеек нарисованы руками, а текст
 * держит одно скрытое поле поверх них — так сохраняются фокус, системная
 * клавиатура и обработка Enter. Все шесть тем PinTheme в Dart одинаковы
 * (default/error/focused/disabled/following/submitted), значит и здесь у
 * ячейки одно оформление на все состояния.
 */

/** `Pinput(length: 6)` */
const PIN_LENGTH = 6;

/** `FilteringTextInputFormatter.digitsOnly` */
const NON_DIGITS = /\D/g;

/**
 * `onCompleted` у Pinput срабатывает на любое изменение контроллера, в том
 * числе извне: на экране PIN цифры набирает экранная клавиатура
 * (`DigitKeyboard`), которая пишет в тот же контроллер.
 */
function useOnComplete(value: string, onComplete?: (value: string) => void): void {
  const wasComplete = useRef(false);
  useEffect(() => {
    const isComplete = value.length === PIN_LENGTH;
    const fired = wasComplete.current;
    wasComplete.current = isComplete;
    if (isComplete && !fired) onComplete?.(value);
  }, [value, onComplete]);
}

export interface PrimaryPinputFieldProps {
  /** Подпись над полем, `AppStyles.s16w600`. */
  title?: string;
  /** Текст контроллера; общий с экранной клавиатурой. */
  value?: string;
  onChange?: (value: string) => void;
  inputRef?: RefObject<HTMLInputElement | null>;
  onComplete?: (value: string) => void;
  onSubmitted?: (value: string) => void;
  /** По умолчанию `true`: цифры приходят с экранной клавиатуры. */
  readOnly?: boolean;
  /** `autofocus:` из Dart. */
  autoFocus?: boolean;
  className?: string;
}

export function PrimaryPinputField(props: PrimaryPinputFieldProps): ReactElement {
  const {
    title, value = '', onChange, inputRef, onComplete, onSubmitted,
    readOnly = true, autoFocus = false, className,
  } = props;

  const [hasFocus, setHasFocus] = useState(false);
  // Единственная разрешённая мутация — `ref.current`: так React устроен.
  const attachInput = useCallback(
    (node: HTMLInputElement | null): void => {
      if (inputRef) inputRef.current = node;
    },
    [inputRef],
  );
  useOnComplete(value, onComplete);

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const digits = event.target.value.replace(NON_DIGITS, '').slice(0, PIN_LENGTH);
    onChange?.(digits);
  };
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key !== 'Enter') return;
    onSubmitted?.(event.currentTarget.value);
  };

  return (
    <div className={[styles.root, className].filter(Boolean).join(' ')}>
      {title !== undefined && <span className={styles.title}>{title}</span>}
      <div className={styles.boxes}>
        <input
          ref={attachInput}
          className={styles.input}
          value={value}
          readOnly={readOnly}
          autoFocus={autoFocus}
          inputMode="numeric"
          enterKeyHint="done"
          maxLength={PIN_LENGTH}
          autoComplete="off"
          onChange={handleChange}
          onFocus={() => setHasFocus(true)}
          onBlur={() => setHasFocus(false)}
          onKeyDown={handleKeyDown}
        />
        <PinCells value={value} showCursor={hasFocus} />
      </div>
    </div>
  );
}

interface PinCellsProps {
  value: string;
  showCursor: boolean;
}

/** Шесть ячеек PinTheme: цифра либо курсор в первой незаполненной. */
function PinCells({ value, showCursor }: PinCellsProps): ReactElement {
  const cells = Array.from({ length: PIN_LENGTH }, (_unused, index) => index);
  return (
    <>
      {cells.map((index) => (
        <div key={index} className={styles.cell}>
          {value[index] ?? ''}
          {showCursor && index === value.length && <span className={styles.cursor} />}
        </div>
      ))}
    </>
  );
}
